import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Save } from "lucide-react";
import { Alert, Button, Card, Page } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { UnsavedChangesModal, useSetTopbarTitle, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { useCurrentCompany } from "../../lib/company";
import { BillingDetailsFields, emptyBillingDetails } from "../../components/BillingDetailsFields";
import { useUpdateCompany, type Company } from "../../lib/queries/companies";
import { SeriesTab } from "./SeriesTab";
import { SettingsTab, type SettingsValues } from "./SettingsTab";
import styles from "./CompanySettingsPage.module.css";

interface CompanyForm extends SettingsValues {
  billing_details: ReturnType<typeof emptyBillingDetails>;
}

function toForm(c: Company): CompanyForm {
  const bd = emptyBillingDetails();
  for (const k of Object.keys(bd) as (keyof typeof bd)[]) {
    (bd as Record<string, unknown>)[k] = c.billing_details[k] ?? bd[k];
  }
  return {
    billing_details: bd,
    default_vat_rate: String(c.default_vat_rate),
    default_due_days: String(c.default_due_days),
    default_quote_validity_days: String(c.default_quote_validity_days),
    default_payment_type: c.default_payment_type,
    vat_period: c.vat_period,
  };
}

/**
 * The legacy company detail (legacy/app/src/resources/Companies.tsx): one
 * card, tabs inside it, the details edited in place and saved from the
 * card's footer. A change of billing details is a new snapshot; documents
 * keep the one they were issued with.
 */
export function CompanySettingsPage() {
  const { company, can } = useCurrentCompany();
  const manager = can("manager");
  const update = useUpdateCompany(company.id);
  const [tab, setTab] = useState("contact");
  useSetTopbarTitle("Společnosti");
  const form = useForm<CompanyForm>({ mode: "onTouched", defaultValues: toForm(company) });
  const guard = useUnsavedChangesGuard(form.formState.isDirty);

  // A save elsewhere (another tab, a refetch) becomes the new baseline.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(toForm(company));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  async function onSubmit(v: CompanyForm) {
    try {
      await update.mutateAsync({
        billing_details: v.billing_details,
        default_vat_rate: Number(v.default_vat_rate),
        default_due_days: Number(v.default_due_days),
        default_quote_validity_days: Number(v.default_quote_validity_days),
        default_payment_type: v.default_payment_type,
        vat_period: v.vat_period,
      });
      form.reset(v);
      notifySuccess("Údaje firmy byly uloženy.");
    } catch (err) {
      apiErrorToFormErrors<CompanyForm>(err, form.setError, { fallbackMessage: "Uložení se nepovedlo." });
    }
  }

  const tabs = [
    { id: "contact", label: "Kontaktní údaje" },
    { id: "settings", label: "Nastavení" },
    { id: "series", label: "Číselné řady" },
  ];
  const formTab = tab === "contact" || tab === "settings";

  return (
    <>
      <Page>
        <Page.Content>
          <FormProvider {...form}>
              <Card>
                <Card.Header title="Detail firmy" subtitle={company.billing_details.business_name} />
                <Card.Tabs tabs={tabs} value={tab} onChange={setTab} aria-label="Detail firmy" />
                {formTab ? (
                  <Card.Content>
                    {/* Scoped to the details tabs: the series dialogs
                        are React children of this card and their submits would
                        bubble into an enclosing form. */}
                    <form id="company-form" noValidate onSubmit={form.handleSubmit(onSubmit)}>
                    <fieldset className={styles.fields} disabled={!manager}>
                      {rootErrorMessage(form.formState.errors) && <Alert variant="err">{rootErrorMessage(form.formState.errors)}</Alert>}
                      {tab === "contact" && <BillingDetailsFields />}
                      {tab === "settings" && <SettingsTab vatPayer={company.billing_details.vat_payer} />}
                    </fieldset>
                    </form>
                  </Card.Content>
                ) : (
                  <Card.Content padding="none">
                    <SeriesTab />
                  </Card.Content>
                )}
                {formTab && manager && (
                  <Card.Footer>
                    <div className={styles.footer}>
                      <Button type="submit" form="company-form" variant="primary" startIcon={<Save size={16} aria-hidden="true" />} loading={update.isPending} disabled={!form.formState.isDirty}>
                        uložit
                      </Button>
                    </div>
                  </Card.Footer>
                )}
              </Card>
          </FormProvider>
        </Page.Content>
      </Page>
      <UnsavedChangesModal open={guard.blocked} onConfirm={guard.proceed} onClose={guard.cancel} />
    </>
  );
}
